/**
 * Catalog attributes → ``<DataTable>`` columns (ADR-038 Phase F).
 *
 * The catalog is the single source of truth for what a column is (feature
 * spec §2.1), so the column set is *derived* rather than declared per
 * type: switching data type re-derives the table rather than looking up a
 * hand-maintained mapping that could drift from the registry.
 *
 * Cell rendering is driven by the attribute's ``kind``, which is why every
 * type gets a coherent presentation without per-type code — timestamps
 * render as UTC to the second, numbers right-align with their unit, enums
 * resolve to their catalog labels rather than showing a bare proto
 * integer, and byte columns render as short hex with the full digest in a
 * tooltip.
 *
 * Cross-references:
 *   - REQ-UI-118 (columns drawn from the type's attributes, defaulting to
 *     the common set); feature spec §2.2 (the common column spine).
 *   - REQ-UI-046 (status colour contrast) — status text uses semantic
 *     tokens, never raw colour.
 */
import type { DataTableColumn } from "@/data-table/data-table-model";
import type { ProducerNameIndex } from "@/lib/use-producer-names";
import {
  type QueryAttribute,
  type QueryRow,
  type QueryableType,
  isDomainAttribute,
} from "@/query/query-model";

/** Characters of a hex digest shown inline before the ellipsis. */
const HEX_PREVIEW_CHARS = 12;

/** Em-dash for an absent value — never an empty cell, which reads as a bug. */
const ABSENT = "—";

/** Format an ISO instant as ``YYYY-MM-DD HH:MM:SS`` UTC (second precision). */
export function formatQueryTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-${pad(parsed.getUTCDate())} ` +
    `${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())}:${pad(parsed.getUTCSeconds())}`
  );
}

/** Resolve an enum integer to its catalog label, falling back to the value. */
export function enumLabel(attribute: QueryAttribute, value: unknown): string {
  const match = attribute.enum_values?.find((option) => option.value === value);
  return match?.label ?? String(value);
}

/** Abbreviate a hex digest for a table cell. */
export function shortHex(value: string): string {
  return value.length <= HEX_PREVIEW_CHARS ? value : `${value.slice(0, HEX_PREVIEW_CHARS)}…`;
}

/** Whether a column should right-align (numeric reading). */
function alignFor(attribute: QueryAttribute): "left" | "right" {
  return attribute.kind === "number" ? "right" : "left";
}

/** Advisory tooltip for a column header, or undefined when none applies. */
function headerTitleFor(attribute: QueryAttribute): string | undefined {
  if (isDomainAttribute(attribute.name)) {
    return `${attribute.label} — a system-status domain metric. Not index-backed; filtering on it over a wide window is slower.`;
  }
  if (!attribute.indexed && attribute.filterable) {
    return `${attribute.label} is not index-backed; filtering on it over a wide window is slower.`;
  }
  return undefined;
}

/**
 * Render one attribute's cell.
 *
 * Exported so the record-detail surface (Phase G) presents the same value
 * the table did — an operator comparing a row to its detail must not see
 * two different renderings of one field.
 */
export function renderQueryValue(
  attribute: QueryAttribute,
  value: unknown,
  producerNames: ProducerNameIndex | null,
): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-text-muted">{ABSENT}</span>;
  }
  switch (attribute.kind) {
    case "timestamp":
      return (
        <span className="font-mono tabular-nums text-text-secondary">
          {formatQueryTime(String(value))}
        </span>
      );
    case "number":
      return (
        <span className="tabular-nums text-text-primary">
          {String(value)}
          {attribute.unit != null && attribute.unit !== "" && (
            <span className="ml-1 text-text-muted">{attribute.unit}</span>
          )}
        </span>
      );
    case "enum":
      return <span className="text-text-primary">{enumLabel(attribute, value)}</span>;
    case "boolean":
      return <span className="text-text-primary">{value === true ? "Yes" : "No"}</span>;
    case "bytes": {
      const hex = String(value);
      return (
        <span className="font-mono text-text-secondary" title={hex}>
          {shortHex(hex)}
        </span>
      );
    }
    case "id": {
      const raw = String(value);
      // producer_id is a UUID; the roster carries the human-readable name
      // (feature spec §2.2). Falls back to the id for a producer the
      // inventory has not caught up with.
      if (attribute.name === "producer_id" && producerNames !== null) {
        const name = producerNames.nameFor(raw);
        return (
          <span className="text-text-primary" title={raw}>
            {name}
          </span>
        );
      }
      return <span className="font-mono text-text-secondary">{raw}</span>;
    }
    default:
      return <span className="text-text-primary">{String(value)}</span>;
  }
}

/**
 * Derive the ``<DataTable>`` column set for a queryable type.
 *
 * Every attribute becomes a column — including the ones the type does not
 * default to showing, so the settings cog can reveal them (REQ-UI-119).
 * Byte columns are included as display-only: they are unfilterable and
 * unsortable, but seeing a record's hash in the table is what lets an
 * operator correlate it with an external artifact.
 */
export function queryColumns(
  type: QueryableType,
  producerNames: ProducerNameIndex | null,
): readonly DataTableColumn<QueryRow>[] {
  return type.attributes.map((attribute) => ({
    key: attribute.name,
    label: attribute.label,
    sortable: attribute.sortable,
    defaultVisible: attribute.default_visible,
    align: alignFor(attribute),
    headerTitle: headerTitleFor(attribute),
    width: attribute.kind === "timestamp" ? 180 : undefined,
    render: (row: QueryRow) => renderQueryValue(attribute, row[attribute.name], producerNames),
  }));
}

/**
 * The record id the detail / byte-window / download routes are keyed by,
 * or ``null`` when this type has no single-record identity.
 *
 * The id column comes from the catalog's ``id_attribute`` rather than a
 * per-type table in the UI — hard-coding it here is exactly the drift the
 * server-side registry exists to prevent. A ``null`` ``id_attribute``
 * means composite identity (the system-status samples), which the BFF
 * gives no detail route: absent capability, absent affordance, so those
 * rows are simply not selectable.
 */
export function recordIdFor(type: QueryableType, row: QueryRow): string | null {
  const name = type.id_attribute;
  if (name === null || name === undefined) return null;
  const value = row[name];
  if (value === null || value === undefined || value === "") return null;
  return String(value);
}

/**
 * The stable per-row key.
 *
 * Result rows carry no single universal id — a status message is keyed by
 * ``(producer_id, time, message_hash)`` while a detection has a ULID — so
 * the key is composed from whatever identity columns the row actually
 * carries, with the row index as the last resort. Getting this wrong shows
 * as React re-using a row's DOM across pages, so it composes rather than
 * guessing one column.
 */
export function queryRowKey(type: QueryableType, row: QueryRow, index: number): string {
  const idAttributes = type.attributes.filter(
    (attribute) => attribute.kind === "id" || attribute.kind === "bytes",
  );
  const parts = idAttributes
    .map((attribute) => row[attribute.name])
    .filter((value) => value !== null && value !== undefined)
    .map((value) => String(value));
  const time = row[type.time_attribute];
  if (time !== null && time !== undefined) parts.push(String(time));
  return parts.length > 0 ? parts.join("|") : `row-${index}`;
}
